import React, { PureComponent } from 'react'
import { parse } from 'query-string'
import { reduxForm, formValues } from 'redux-form'
import track from 'react-tracking'
import ReactMarkdown from 'markdown-to-jsx'

import BEMHelper from '@ynap/bem-helper'
import Field from '@ynap/field'
import { SimpleAsyncButton } from '@ynap/button'
import { submitAndCollateErrorsPromise, onSubmitFail } from '@ynap/form-utils'
import { Panel } from '@ynap/login-and-registration-form'
import Notification from '@ynap/notification'
import { Link } from '@ynap/router'
import Skeleton from '@ynap/skeleton'

import getUserSummary from 'app/state/myaccount-state/actions/getUserSummary'
import validate from './validate'
import { myaccountPageData } from '../analytics'
import { trackGuestOrderTrackSuccess, trackGuestOrderTrackError, mapTypeToValue } from './analytics'

const bem = new BEMHelper('GuestOrders')

@track()
export class GuestOrders extends PureComponent {
    constructor() {
        super()
        this.submit = this.submit.bind(this)
        this.openSignInDialog = this.openSignInDialog.bind(this)
    }

    state = {
        orderId: '',
        isUserSummaryLoaded: false,
    }

    openSignInDialog(event) {
        event.preventDefault()
        const { locale, type } = this.props
        const pageType = type === 'createReturn' ? 'return' : 'track'
        window.SF.openSignInDialog(`guest orders ${pageType} page`).then((response) => {
            if (response) {
                window?.location.replace(`/${locale.id}/account/orders/`)
            }
        })
    }

    submit(values) {
        const { fetchOrderDetails } = this.props
        const { externalOrderId, email } = values
        this.setState({ orderId: externalOrderId })

        window.localStorage.setItem('guestOrderEmail', email)
        window.localStorage.setItem('guestOrderId', externalOrderId)

        return submitAndCollateErrorsPromise(fetchOrderDetails(externalOrderId, true, email), values, this.props)
    }

    componentDidUpdate(prevProps) {
        const {
            type,
            isExchangable,
            isReturnable,
            locale,
            push,
            submitting,
            fetchOrderDetailsError,
            submitSucceeded,
            tracking,
            messages,
            orderDate,
            isFetchingOrderDetails,
        } = this.props
        const { orderId } = this.state
        const isReturnOrExchange = isReturnable === 'true' || isExchangable
        const orderReady = !isFetchingOrderDetails && submitSucceeded && orderId && !fetchOrderDetailsError
        let redirectUrl

        if (!prevProps.fetchOrderDetailsError && fetchOrderDetailsError)
            tracking.trackEvent(
                trackGuestOrderTrackError(mapTypeToValue(type), messages.guestOrderTracking[type].error())
            )

        if (orderReady) {
            if (type !== 'createReturn') {
                redirectUrl = `/${locale.id}/account/guest-order/${orderId}`
                tracking.trackEvent(trackGuestOrderTrackSuccess(mapTypeToValue(type)))
            } else if (isReturnOrExchange && type === 'createReturn') {
                redirectUrl = `/${locale.id}/account/guest-order/${orderId}/return`
            }

            redirectUrl && push(redirectUrl)
        }
    }

    @track((props) => myaccountPageData({ name: mapTypeToValue(props.type) })(props))
    componentDidMount() {
        const { locale, push, config } = this.props
        const {
            head: { flags: { enableLuxuryGuestOrders } = false },
        } = config

        if (enableLuxuryGuestOrders) {
            getUserSummary(locale, false).then((userSummary) => {
                if (userSummary) {
                    let redirectUrl = `/${locale.id}/account/orders/`
                    push(redirectUrl)
                } else {
                    this.setState({ isUserSummaryLoaded: true })
                }
            })
        }

        const { orderNumber } = parse(window?.location?.search)
        if (orderNumber) {
            this.props.change('externalOrderId', orderNumber)
        }
    }

    render() {
        const {
            locale,
            messages,
            type,
            form,
            handleSubmit,
            submitting,
            fetchOrderDetailsError,
            externalOrderId,
            isReturnable,
            isExchangable,
            orderDate,
            config,
            nativeApp,
            rpiRequired,
        } = this.props
        const { guestOrderTracking } = messages
        const { orderId, isUserSummaryLoaded } = this.state
        const {
            head: { flags: { enableLuxuryGuestOrders } = false, returnWindow = {} },
        } = config
        const typeMessages = guestOrderTracking[type]
        const isReturnOrExchange = isReturnable === 'true' || isExchangable
        const showNotification =
            !isReturnOrExchange && !submitting && orderDate && orderId === externalOrderId && !fetchOrderDetailsError

        const window = returnWindow?.countries?.includes?.(locale.country)
            ? returnWindow?.specialWindow
            : returnWindow?.default

        if (!isUserSummaryLoaded && enableLuxuryGuestOrders) {
            return (
                <div className={bem()}>
                    <Panel>
                        <Skeleton align="center" className={bem('skeletonTitle')} />
                        <Skeleton count={2} align="center" className={bem('skeletonLine')} />
                        <div className={bem('skeletonFormWrapper')}>
                            <Skeleton count={2} align="center" className={bem('skeletonInput')} />
                            <Skeleton count={1} align="center" className={bem('skeletonCTA')} />
                        </div>
                    </Panel>
                </div>
            )
        } else {
            return (
                <div className={bem()}>
                    <Panel className={bem('GuestOrdersPanel')} title={typeMessages.title()}>
                        <div className={bem('info')}>
                            {typeMessages.info({
                                returnWindow: window,
                            })}
                        </div>
                        <div className={bem('helpInfo')}>
                            <ReactMarkdown
                                options={{
                                    overrides: {
                                        a: (props) => (
                                            <a
                                                className={bem('link')}
                                                href={props.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {props.children}
                                            </a>
                                        ),
                                    },
                                }}
                            >
                                {`${rpiRequired ? typeMessages.proformaInfo() : ''}${typeMessages.helpInfo({
                                    locale: locale.id,
                                })}`}
                            </ReactMarkdown>
                        </div>
                        {fetchOrderDetailsError && !submitting && (
                            <Notification className={bem('message')} type="error" message={typeMessages.error()} />
                        )}
                        {type === 'createReturn' && showNotification && (
                            <Notification
                                className={bem('message')}
                                type="info"
                                message={
                                    <>
                                        {typeMessages.notReturnable()}
                                        <b>
                                            <Link to={`/${locale.id}/account/guest-order/${orderId}`}>{orderId}</Link>
                                        </b>
                                    </>
                                }
                            />
                        )}

                        <form id={form} className={bem('form')} onSubmit={handleSubmit(this.submit)}>
                            <Field
                                name="email"
                                type="email"
                                label={typeMessages.lables.email()}
                                autoComplete="username"
                            />
                            <Field name="externalOrderId" type="text" label={typeMessages.lables.orderId()} />
                            <SimpleAsyncButton
                                className={bem('submit')}
                                loading={submitting}
                                copy={typeMessages.lables.button()}
                            />
                            {enableLuxuryGuestOrders && !nativeApp && (
                                <div className={bem('signInWrapper')}>
                                    <span className={bem('signInParagraph')}>{typeMessages.signInParagraph()}</span>
                                    <button
                                        onClick={(event) => this.openSignInDialog(event)}
                                        className={bem('signInLink')}
                                    >
                                        {typeMessages.signIn()}
                                    </button>
                                </div>
                            )}
                        </form>
                    </Panel>
                </div>
            )
        }
    }
}

const GuestOrdersWithValues = formValues('email', 'externalOrderId')(GuestOrders)
export default reduxForm({ form: 'guestOrders', validate, onSubmitFail })(GuestOrdersWithValues)
